import {CheckoutEventsItem, CheckoutEventsTimePeriodInterval} from "@paddle/paddle-js";

import type {CheckoutPeriod, PriceDetails} from "./types";


interface PricingComputation {
    items: CheckoutEventsItem[];
    priceDetails: PriceDetails;
}

// --- USD pricing (placeholder / Paddle preview) ---

const USD_PRICING_TABLE = {
    monthly: {
        locationSeat: import.meta.env.VITE_APP_STD_LOC_MONTHLY_USD,
        userSeat: import.meta.env.VITE_APP_STD_USR_MONTHLY_USD,
    },
    yearly: {
        locationSeat: import.meta.env.VITE_APP_STD_LOC_YEARLY_USD,
        userSeat: import.meta.env.VITE_APP_STD_USR_YEARLY_USD
    }
} as const;

const PRICE_IDS = {
    base: {
        monthly: import.meta.env.VITE_APP_PADDLE_STD_MONTHLY_ID,
        yearly: import.meta.env.VITE_APP_PADDLE_STD_YEARLY_ID,
    },
    user: {
        monthly: import.meta.env.VITE_APP_PADDLE_STD_USER_MONTHLY_ID,
        yearly: import.meta.env.VITE_APP_PADDLE_STD_USER_YEARLY_ID,
    },
    location: {
        monthly: import.meta.env.VITE_APP_PADDLE_STD_LOCATION_MONTHLY_ID,
        yearly: import.meta.env.VITE_APP_PADDLE_STD_LOCATION_YEARLY_ID,
    }
} as const;

export interface UsdPricingInput {
    period: CheckoutPeriod;
    locationSlotsCount: number;
    billableUserSeats: number;
    prices: Record<CheckoutPeriod, {
        locationSeat: number,
        userSeat: number,
    }>
}

export const calculatePricing = ({
                                     period,
                                     locationSlotsCount,
                                     billableUserSeats,
                                     prices
                                 }: UsdPricingInput): PricingComputation => {
    const pricing = prices[period];

    const interval = period === 'monthly'
        ? CheckoutEventsTimePeriodInterval.MONTH
        : CheckoutEventsTimePeriodInterval.YEAR;

    const locationSlotsCost = locationSlotsCount * pricing.locationSeat;
    const userSeatsCost = billableUserSeats * pricing.userSeat;

    const subtotal = locationSlotsCost + userSeatsCost;

    const items: CheckoutEventsItem[] = [];

    const locationPriceId = PRICE_IDS.location[period];
    if (locationSlotsCount > 0 && locationPriceId) {
        items.push(createCheckoutItem({
            priceId: locationPriceId,
            quantity: locationSlotsCount,
            cost: locationSlotsCost,
            interval,
        }));
    }

    const userPriceId = PRICE_IDS.user[period];
    if (billableUserSeats > 0 && userPriceId) {
        items.push(createCheckoutItem({
            priceId: userPriceId,
            quantity: billableUserSeats,
            cost: userSeatsCost,
            interval,
        }));
    }

    const priceDetails: PriceDetails = {
        subtotal,
        tax: 0,
        total: subtotal,
    };

    return {
        items,
        priceDetails,
    };
};

export const calculateUsdPricing = (params: RubPricingInput): PricingComputation => {
    return calculatePricing({...params, prices: USD_PRICING_TABLE})
};

const RUB_PRICE_TABLE = {
    monthly: {
        userSeat: import.meta.env.VITE_APP_STD_USR_MONTHLY_RUB,
        locationSeat: import.meta.env.VITE_APP_STD_LOC_MONTHLY_RUB,
    },
    yearly: {
        userSeat: import.meta.env.VITE_APP_STD_USR_YEARLY_RUB,
        locationSeat: import.meta.env.VITE_APP_STD_LOC_YEARLY_RUB,
    }
} as const;

export interface RubPricingInput {
    period: CheckoutPeriod;
    locationSlotsCount: number;
    billableUserSeats: number;
}

export const calculateRubPricing = (params: RubPricingInput): PricingComputation => {
    return calculatePricing({...params, prices: RUB_PRICE_TABLE})
};

interface CreateCheckoutItemArgs {
    priceId: string;
    quantity: number;
    cost: number;
    interval: CheckoutEventsTimePeriodInterval;
}

const createCheckoutItem = ({
                                priceId,
                                quantity,
                                cost,
                                interval,
                            }: CreateCheckoutItemArgs): CheckoutEventsItem => ({
    quantity,
    price_id: priceId,
    billing_cycle: {
        interval,
        frequency: 1,
    },
    totals: {
        discount: 0,
        subtotal: cost,
        tax: 0,
        total: cost,
    },
    product: {
        id: priceId,
        name: '',
        description: '',
    }
});

export type {PricingComputation};
